import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { formatCurrency } from '../lib/currency';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Plus,
  DollarSign,
  Clock,
  CheckCircle,
  AlertTriangle,
  CreditCard,
  Pencil,
  Trash2,
  MoreVertical
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { CURRENCY_DATA } from '../lib/currency';
import { toast } from 'sonner';
import { PermissionBanner } from '../components/PermissionBanner';
import { showPermissionDenied } from '../lib/utils';

const Receivables = () => {
  const { company, canEdit, isViewer } = useAuth();
  const [activeTab, setActiveTab] = useState('receivables');
  const [receivables, setReceivables] = useState([]);
  const [payables, setPayables] = useState([]);
  const [receivablesSummary, setReceivablesSummary] = useState(null);
  const [payablesSummary, setPayablesSummary] = useState(null);
  const [counterparties, setCounterparties] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showAddReceivable, setShowAddReceivable] = useState(false);
  const [showAddPayable, setShowAddPayable] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [paymentType, setPaymentType] = useState(null); // 'receivable' or 'payable'
  const [contactType, setContactType] = useState('customer'); // for quick add
  
  // Form states
  const [formData, setFormData] = useState({
    counterparty_id: '',
    amount: '',
    currency: 'USD',
    description: '',
    due_date: ''
  });
  
  // Update default currency when company data loads
  useEffect(() => {
    if (company?.currency) {
      setFormData(prev => ({
        ...prev,
        currency: prev.currency === 'USD' ? company.currency : prev.currency
      }));
    }
  }, [company?.currency]);
  
  // Edit mode states
  const [editMode, setEditMode] = useState(null); // 'receivable' | 'payable'
  const [editingItem, setEditingItem] = useState(null);
  
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_account_id: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_reference: ''
  });
  
  const [newContact, setNewContact] = useState({
    name: '',
    counterparty_type: 'customer',
    email: '',
    phone: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [recRes, payRes, recSumRes, paySumRes, cpRes, accRes] = await Promise.all([
        api.get('/receivables'),
        api.get('/payables'),
        api.get('/receivables/summary'),
        api.get('/payables/summary'),
        api.get('/counterparties'),
        api.get('/accounts')
      ]);
      setReceivables(recRes.data);
      setPayables(payRes.data);
      setReceivablesSummary(recSumRes.data);
      setPayablesSummary(paySumRes.data);
      setCounterparties(cpRes.data);
      setAccounts(accRes.data.filter(a => ['cash', 'bank', 'mobile_money'].includes(a.account_subtype)));
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReceivable = async () => {
    if (!formData.counterparty_id || !formData.amount) {
      toast.error('Please fill in required fields');
      return;
    }
    try {
      if (editingItem) {
        await api.put(`/receivables/${editingItem.id}`, {
          ...formData,
          amount: parseFloat(formData.amount),
          due_date: formData.due_date || null
        });
        toast.success('Receivable updated successfully');
      } else {
        await api.post('/receivables', {
          ...formData,
          amount: parseFloat(formData.amount),
          due_date: formData.due_date || null
        });
        toast.success('Receivable created successfully');
      }
      setShowAddReceivable(false);
      setEditingItem(null);
      setEditMode(null);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save receivable');
    }
  };

  const handleCreatePayable = async () => {
    if (!formData.counterparty_id || !formData.amount) {
      toast.error('Please fill in required fields');
      return;
    }
    try {
      if (editingItem) {
        await api.put(`/payables/${editingItem.id}`, {
          ...formData,
          amount: parseFloat(formData.amount),
          due_date: formData.due_date || null
        });
        toast.success('Payable updated successfully');
      } else {
        await api.post('/payables', {
          ...formData,
          amount: parseFloat(formData.amount),
          due_date: formData.due_date || null
        });
        toast.success('Payable created successfully');
      }
      setShowAddPayable(false);
      setEditingItem(null);
      setEditMode(null);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save payable');
    }
  };

  const handleDeleteReceivable = async (id) => {
    if (!confirm('Are you sure you want to delete this receivable?')) return;
    try {
      await api.delete(`/receivables/${id}`);
      toast.success('Receivable deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete receivable');
    }
  };

  const handleDeletePayable = async (id) => {
    if (!confirm('Are you sure you want to delete this payable?')) return;
    try {
      await api.delete(`/payables/${id}`);
      toast.success('Payable deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete payable');
    }
  };

  const openEditReceivable = (rec) => {
    setEditingItem(rec);
    setEditMode('receivable');
    setFormData({
      counterparty_id: rec.counterparty_id || '',
      amount: rec.amount?.toString() || '',
      currency: rec.currency || 'USD',
      description: rec.description || '',
      due_date: rec.due_date || ''
    });
    setShowAddReceivable(true);
  };

  const openEditPayable = (pay) => {
    setEditingItem(pay);
    setEditMode('payable');
    setFormData({
      counterparty_id: pay.counterparty_id || '',
      amount: pay.amount?.toString() || '',
      currency: pay.currency || 'USD',
      description: pay.description || '',
      due_date: pay.due_date || ''
    });
    setShowAddPayable(true);
  };

  const handleRecordPayment = async () => {
    if (!paymentData.amount || !paymentData.payment_account_id) {
      toast.error('Please fill in required fields');
      return;
    }
    try {
      const endpoint = paymentType === 'receivable' 
        ? `/receivables/${selectedItem.id}/payments`
        : `/payables/${selectedItem.id}/payments`;
      
      await api.post(endpoint, {
        ...paymentData,
        amount: parseFloat(paymentData.amount)
      });
      
      toast.success('Payment recorded successfully');
      setShowPaymentDialog(false);
      setSelectedItem(null);
      setPaymentType(null);
      setPaymentData({
        amount: '',
        payment_account_id: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_reference: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const resetForm = () => {
    setFormData({
      counterparty_id: '',
      amount: '',
      currency: company?.currency || 'USD',
      description: '',
      due_date: ''
    });
    setEditingItem(null);
    setEditMode(null);
  };

  const handleQuickAddContact = async () => {
    if (!newContact.name) {
      toast.error('Please enter a name');
      return;
    }
    try {
      const res = await api.post('/counterparties', newContact);
      toast.success(`${newContact.counterparty_type === 'customer' ? 'Customer' : 'Supplier'} added successfully`);
      setShowAddContact(false);
      // Auto-select the new contact
      setFormData({ ...formData, counterparty_id: res.data.id });
      setNewContact({ name: '', counterparty_type: contactType, email: '', phone: '' });
      // Refresh counterparties list
      const cpRes = await api.get('/counterparties');
      setCounterparties(cpRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add contact');
    }
  };

  const openQuickAddContact = (type) => {
    setContactType(type);
    setNewContact({ name: '', counterparty_type: type, email: '', phone: '' });
    setShowAddContact(true);
  };

  const openPaymentDialog = (item, type) => {
    setSelectedItem(item);
    setPaymentType(type);
    const balance = parseFloat(item.amount) - parseFloat(item.amount_paid || 0);
    setPaymentData({
      amount: balance.toFixed(2),
      payment_account_id: accounts.length > 0 ? accounts[0].id : '',
      payment_date: new Date().toISOString().split('T')[0],
      payment_reference: ''
    });
    setShowPaymentDialog(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-[hsl(var(--warning-soft))] text-[hsl(var(--warning))]',
      partial: 'bg-[hsl(var(--brand-100))] text-[hsl(var(--brand-700))]',
      paid: 'bg-[hsl(var(--success-soft))] text-[hsl(var(--success))]',
      written_off: 'bg-gray-100 text-gray-600'
    };
    return (
      <Badge className={styles[status] || 'bg-gray-100'}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </Badge>
    );
  };

  const formatAmount = (amount) => {
    return formatCurrency(amount || 0, company?.currency || 'USD');
  };

  const customers = counterparties.filter(c => c.counterparty_type === 'customer');
  const suppliers = counterparties.filter(c => c.counterparty_type === 'supplier');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[hsl(var(--brand-500))]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="receivables-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[hsl(var(--ink-1))]" style={{ fontFamily: 'Space Grotesk' }}>
            Receivables & Payables
          </h1>
          <p className="text-[hsl(var(--muted-ink))]">
            Track money owed to you and money you owe
          </p>
        </div>
      </div>

      {/* Permission Banner for Viewers */}
      <PermissionBanner 
        show={isViewer} 
        message="You have read-only access to receivables and payables. Contact an admin to request edit permissions."
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="receivables" className="flex items-center gap-2" data-testid="tab-receivables">
            <ArrowDownCircle size={16} />
            Receivables
          </TabsTrigger>
          <TabsTrigger value="payables" className="flex items-center gap-2" data-testid="tab-payables">
            <ArrowUpCircle size={16} />
            Payables
          </TabsTrigger>
        </TabsList>

        {/* Receivables Tab */}
        <TabsContent value="receivables" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[hsl(var(--muted-ink))]" data-testid="rec-outstanding-label">Outstanding</p>
                    <p className="text-2xl font-bold text-[hsl(var(--ink-1))]" data-testid="rec-outstanding-value">
                      {formatAmount(receivablesSummary?.outstanding)}
                    </p>
                  </div>
                  <DollarSign className="h-8 w-8 text-[hsl(var(--brand-500))]" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[hsl(var(--muted-ink))]">Open</p>
                    <p className="text-2xl font-bold">{receivablesSummary?.by_status?.open || 0}</p>
                  </div>
                  <Clock className="h-8 w-8 text-[hsl(var(--warning))]" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[hsl(var(--muted-ink))]">Overdue</p>
                    <p className="text-2xl font-bold text-[hsl(var(--danger))]">{receivablesSummary?.by_status?.overdue || 0}</p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-[hsl(var(--danger))]" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[hsl(var(--muted-ink))]">Collected</p>
                    <p className="text-2xl font-bold text-[hsl(var(--success))]">
                      {formatAmount(receivablesSummary?.total_paid)}
                    </p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-[hsl(var(--success))]" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Add Button & List */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Money Owed to You</CardTitle>
              {canEdit && (
                <Button 
                  onClick={() => setShowAddReceivable(true)}
                  className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))]"
                  data-testid="add-receivable-btn"
                >
                  <Plus size={16} className="mr-2" />
                  Someone Owes Me
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {receivables.length === 0 ? (
                <div className="text-center py-12">
                  <ArrowDownCircle className="mx-auto h-12 w-12 text-[hsl(var(--muted-ink))]" />
                  <h3 className="mt-4 text-lg font-medium">No receivables yet</h3>
                  <p className="mt-2 text-[hsl(var(--muted-ink))]">Record money owed to you by customers</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {receivables.map((rec) => (
                    <div 
                      key={rec.id} 
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-[hsl(var(--surface-2))] transition-colors"
                      data-testid={`receivable-item-${rec.id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <p className="font-medium">{rec.counterparty?.name || 'Unknown'}</p>
                          {getStatusBadge(rec.status)}
                        </div>
                        <p className="text-sm text-[hsl(var(--muted-ink))]">
                          {rec.description || 'No description'}
                          {rec.due_date && ` • Due: ${new Date(rec.due_date).toLocaleDateString()}`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-[hsl(var(--success))]">{formatCurrency(rec.display_amount ?? rec.amount, rec.display_currency || company?.currency || 'USD')}</p>
                        {parseFloat(rec.amount_paid || 0) > 0 && (
                          <p className="text-sm text-[hsl(var(--muted-ink))]">
                            Paid: {formatCurrency(rec.display_paid ?? rec.amount_paid, rec.display_currency || company?.currency || 'USD')}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        {rec.status !== 'paid' && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => canEdit ? openPaymentDialog(rec, 'receivable') : showPermissionDenied(toast, 'record payments', 'receivables')}
                            data-testid={`receive-payment-btn-${rec.id}`}
                            disabled={!canEdit}
                          >
                            <CreditCard size={14} className="mr-1" />
                            Receive
                          </Button>
                        )}
                        {canEdit && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`receivable-menu-${rec.id}`}>
                                <MoreVertical size={16} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditReceivable(rec)} data-testid={`edit-receivable-${rec.id}`}>
                                <Pencil size={14} className="mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleDeleteReceivable(rec.id)} 
                                className="text-red-600"
                                data-testid={`delete-receivable-${rec.id}`}
                              >
                                <Trash2 size={14} className="mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payables Tab */}
        <TabsContent value="payables" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[hsl(var(--muted-ink))]" data-testid="pay-outstanding-label">Outstanding</p>
                    <p className="text-2xl font-bold text-[hsl(var(--danger))]" data-testid="pay-outstanding-value">
                      {formatAmount(payablesSummary?.outstanding)}
                    </p>
                  </div>
                  <DollarSign className="h-8 w-8 text-[hsl(var(--danger))]" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[hsl(var(--muted-ink))]">Open</p>
                    <p className="text-2xl font-bold">{payablesSummary?.by_status?.open || 0}</p>
                  </div>
                  <Clock className="h-8 w-8 text-[hsl(var(--warning))]" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[hsl(var(--muted-ink))]">Overdue</p>
                    <p className="text-2xl font-bold text-[hsl(var(--danger))]">{payablesSummary?.by_status?.overdue || 0}</p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-[hsl(var(--danger))]" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[hsl(var(--muted-ink))]">Paid Out</p>
                    <p className="text-2xl font-bold text-[hsl(var(--success))]">
                      {formatAmount(payablesSummary?.total_paid)}
                    </p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-[hsl(var(--success))]" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Add Button & List */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Money You Owe</CardTitle>
              {canEdit && (
                <Button 
                  onClick={() => setShowAddPayable(true)}
                  className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))]"
                  data-testid="add-payable-btn"
                >
                  <Plus size={16} className="mr-2" />
                  I Owe Someone
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {payables.length === 0 ? (
                <div className="text-center py-12">
                  <ArrowUpCircle className="mx-auto h-12 w-12 text-[hsl(var(--muted-ink))]" />
                  <h3 className="mt-4 text-lg font-medium">No payables yet</h3>
                  <p className="mt-2 text-[hsl(var(--muted-ink))]">Record money you owe to suppliers</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {payables.map((pay) => (
                    <div 
                      key={pay.id} 
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-[hsl(var(--surface-2))] transition-colors"
                      data-testid={`payable-item-${pay.id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <p className="font-medium">{pay.counterparty?.name || 'Unknown'}</p>
                          {getStatusBadge(pay.status)}
                        </div>
                        <p className="text-sm text-[hsl(var(--muted-ink))]">
                          {pay.description || 'No description'}
                          {pay.due_date && ` • Due: ${new Date(pay.due_date).toLocaleDateString()}`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-[hsl(var(--danger))]">{formatCurrency(pay.display_amount ?? pay.amount, pay.display_currency || company?.currency || 'USD')}</p>
                        {parseFloat(pay.amount_paid || 0) > 0 && (
                          <p className="text-sm text-[hsl(var(--muted-ink))]">
                            Paid: {formatCurrency(pay.display_paid ?? pay.amount_paid, pay.display_currency || company?.currency || 'USD')}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        {pay.status !== 'paid' && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => canEdit ? openPaymentDialog(pay, 'payable') : showPermissionDenied(toast, 'make payments', 'payables')}
                            data-testid={`make-payment-btn-${pay.id}`}
                            disabled={!canEdit}
                          >
                            <CreditCard size={14} className="mr-1" />
                            Pay
                          </Button>
                        )}
                        {canEdit && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`payable-menu-${pay.id}`}>
                                <MoreVertical size={16} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditPayable(pay)} data-testid={`edit-payable-${pay.id}`}>
                                <Pencil size={14} className="mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleDeletePayable(pay.id)} 
                                className="text-red-600"
                                data-testid={`delete-payable-${pay.id}`}
                              >
                                <Trash2 size={14} className="mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Receivable Dialog */}
      <Dialog open={showAddReceivable} onOpenChange={(open) => { setShowAddReceivable(open); if (!open) resetForm(); }}>
        <DialogContent data-testid="add-receivable-dialog">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Receivable' : 'Record Money Owed to You'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Customer *</Label>
                <Button 
                  type="button" 
                  variant="link" 
                  size="sm" 
                  className="h-auto p-0 text-[hsl(var(--brand-600))]"
                  onClick={() => openQuickAddContact('customer')}
                  data-testid="quick-add-customer-btn"
                >
                  <Plus size={14} className="mr-1" />
                  Add New Customer
                </Button>
              </div>
              <Select 
                value={formData.counterparty_id} 
                onValueChange={(v) => setFormData({...formData, counterparty_id: v})}
              >
                <SelectTrigger data-testid="receivable-customer-select">
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.length === 0 ? (
                    <SelectItem value="none" disabled>No customers - click Add New above</SelectItem>
                  ) : (
                    customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  placeholder="0.00"
                  data-testid="receivable-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select 
                  value={formData.currency} 
                  onValueChange={(v) => setFormData({...formData, currency: v})}
                >
                  <SelectTrigger data-testid="receivable-currency-select">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CURRENCY_DATA).map(([code, data]) => (
                      <SelectItem key={code} value={code}>
                        {data.symbol} {code} - {data.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="What is this for?"
                data-testid="receivable-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                data-testid="receivable-due-date-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddReceivable(false); resetForm(); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateReceivable}
              className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))]"
              data-testid="save-receivable-btn"
            >
              {editingItem ? 'Update Receivable' : 'Save Receivable'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Payable Dialog */}
      <Dialog open={showAddPayable} onOpenChange={(open) => { setShowAddPayable(open); if (!open) resetForm(); }}>
        <DialogContent data-testid="add-payable-dialog">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Payable' : 'Record Money You Owe'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Supplier *</Label>
                <Button 
                  type="button" 
                  variant="link" 
                  size="sm" 
                  className="h-auto p-0 text-[hsl(var(--brand-600))]"
                  onClick={() => openQuickAddContact('supplier')}
                  data-testid="quick-add-supplier-btn"
                >
                  <Plus size={14} className="mr-1" />
                  Add New Supplier
                </Button>
              </div>
              <Select 
                value={formData.counterparty_id} 
                onValueChange={(v) => setFormData({...formData, counterparty_id: v})}
              >
                <SelectTrigger data-testid="payable-supplier-select">
                  <SelectValue placeholder="Select supplier" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.length === 0 ? (
                    <SelectItem value="none" disabled>No suppliers - click Add New above</SelectItem>
                  ) : (
                    suppliers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  placeholder="0.00"
                  data-testid="payable-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select 
                  value={formData.currency} 
                  onValueChange={(v) => setFormData({...formData, currency: v})}
                >
                  <SelectTrigger data-testid="payable-currency-select">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CURRENCY_DATA).map(([code, data]) => (
                      <SelectItem key={code} value={code}>
                        {data.symbol} {code} - {data.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="What is this for?"
                data-testid="payable-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                data-testid="payable-due-date-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddPayable(false); resetForm(); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreatePayable}
              className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))]"
              data-testid="save-payable-btn"
            >
              {editingItem ? 'Update Payable' : 'Save Payable'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle>
              {paymentType === 'receivable' ? 'Receive Payment' : 'Make Payment'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedItem && (
              <div className="p-3 bg-[hsl(var(--surface-2))] rounded-lg">
                <p className="font-medium">{selectedItem.counterparty?.name}</p>
                <p className="text-sm text-[hsl(var(--muted-ink))]">
                  Total: {formatAmount(selectedItem.amount)} • 
                  Paid: {formatAmount(selectedItem.amount_paid || 0)} • 
                  Balance: {formatAmount(parseFloat(selectedItem.amount) - parseFloat(selectedItem.amount_paid || 0))}
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Payment Amount *</Label>
              <Input
                type="number"
                step="0.01"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({...paymentData, amount: e.target.value})}
                placeholder="0.00"
                data-testid="payment-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{paymentType === 'receivable' ? 'Deposit To' : 'Pay From'} *</Label>
              <Select 
                value={paymentData.payment_account_id} 
                onValueChange={(v) => setPaymentData({...paymentData, payment_account_id: v})}
              >
                <SelectTrigger data-testid="payment-account-select">
                  <SelectValue placeholder="Select account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Payment Date *</Label>
              <Input
                type="date"
                value={paymentData.payment_date}
                onChange={(e) => setPaymentData({...paymentData, payment_date: e.target.value})}
                data-testid="payment-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Reference</Label>
              <Input
                value={paymentData.payment_reference}
                onChange={(e) => setPaymentData({...paymentData, payment_reference: e.target.value})}
                placeholder="Check #, Transfer ID, etc."
                data-testid="payment-reference-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRecordPayment}
              className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))]"
              data-testid="confirm-payment-btn"
            >
              {paymentType === 'receivable' ? 'Record Receipt' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Add Contact Dialog */}
      <Dialog open={showAddContact} onOpenChange={setShowAddContact}>
        <DialogContent data-testid="quick-add-contact-dialog">
          <DialogHeader>
            <DialogTitle>
              Add New {contactType === 'customer' ? 'Customer' : 'Supplier'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={newContact.name}
                onChange={(e) => setNewContact({...newContact, name: e.target.value})}
                placeholder={contactType === 'customer' ? 'Customer name' : 'Supplier name'}
                data-testid="contact-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Email (optional)</Label>
              <Input
                type="email"
                value={newContact.email}
                onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                placeholder="email@example.com"
                data-testid="contact-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone (optional)</Label>
              <Input
                value={newContact.phone}
                onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
                placeholder="+1 234 567 8900"
                data-testid="contact-phone-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddContact(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleQuickAddContact}
              className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))]"
              data-testid="save-contact-btn"
            >
              Add {contactType === 'customer' ? 'Customer' : 'Supplier'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Receivables;
